/**
 * Carlink AI — AI-сводка по лиду осмотра для карточки.
 *
 * Данные собирает и псевдонимизирует edge-функция `lead-summary` (репо stryzh):
 * лид + заказ + слитая лента комментариев (без имени/тел клиента) → ai-proxy →
 * Sonnet 4.6. Ответ кэшируется в `lead_ai_summaries` по fingerprint «последнего
 * изменения»; `force: true` форсит пересборку (кнопка «обновить»).
 */
import { createClient } from '@/api/supabase'
import i18n from '@/utils/i18n'

export type CarlinkFlagKind = 'ok' | 'warn' | 'danger' | 'info'

export interface CarlinkFlag {
  kind: CarlinkFlagKind
  text: string
}

export interface CarlinkSummary {
  tldr: string
  flags: CarlinkFlag[]
  /** Пусто у профиля СберАвто (решение 01.11 — без «следующего шага»). */
  next_step: string
  /** «Висит»: открытые вопросы и обещания — профиль СберАвто. */
  open?: string[]
}

export interface CarlinkSummaryResult {
  summary: CarlinkSummary
  cached: boolean
  generated_at: string
  model: string | null
}

/** Состояние сводки «Комментарий для СберАвто» (kind partner): ошибка
 *  Carlink AI видна в карточке, партнёру при ошибке ничего не уходит. */
export async function getPartnerCommentAiState(
  leadId: string,
): Promise<{ error: string | null; error_at: string | null; generated_at: string | null } | null> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('lead_ai_summaries')
    .select('error, error_at, generated_at')
    .eq('lead_id', leadId)
    .eq('kind', 'partner')
    .maybeSingle()
  if (error) throw new Error(error.message)
  return data
}

/**
 * kind: status — статус лида по данным системы (сверху карточки); chat — сводка
 * переписки чата команды (СберАвто, блок «Из чата команды»). Источники не смешиваются.
 */
export async function getLeadSummary(
  leadId: string,
  force = false,
  kind: 'status' | 'chat' = 'status',
): Promise<CarlinkSummaryResult> {
  const supabase = createClient()
  const { data, error } = await supabase.functions.invoke('lead-summary', {
    body: { lead_id: leadId, force, kind },
  })
  if (error) throw new Error(error.message)
  const res = data as Partial<CarlinkSummaryResult> & { error?: string }
  if (res?.error) throw new Error(res.error)
  if (!res?.summary) throw new Error(i18n.t('apiErr.leadSummary.empty'))
  return res as CarlinkSummaryResult
}
